import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import { selectDatasourceCols } from '../../redux/datasourceSlice.js';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import SettingsSection from '../../settings_sidebar/common/SettingsSection.jsx';
import SelectSetting from '../../settings_sidebar/common/SelectSetting.jsx';
import Select from '../../../common/Select.jsx';
import Button from '../../../bootstrap/Button.jsx';
import { FaIcon } from '../../../common/Icons.jsx';
import { useDashboardPresets } from '../../hooks/useDashboardPresets.js';
import ToggleSetting from '../../settings_sidebar/ag_chart/components/ToggleSetting.jsx';

const BUTTON_SIZE_OPTIONS = [
  { value: 'xs', label: I18n.t('extra_small') },
  { value: 'sm', label: I18n.t('small') },
  { value: 'md', label: I18n.t('medium') },
  { value: 'lg', label: I18n.t('large') },
];

const ALIGNMENT_OPTIONS = [
  { value: 'top-left', label: I18n.t('top_left') },
  { value: 'top-center', label: I18n.t('top_center') },
  { value: 'top-right', label: I18n.t('top_right') },
  { value: 'center-left', label: I18n.t('center_left') },
  { value: 'center', label: I18n.t('center') },
  { value: 'center-right', label: I18n.t('center_right') },
  { value: 'bottom-left', label: I18n.t('bottom_left') },
  { value: 'bottom-center', label: I18n.t('bottom_center') },
  { value: 'bottom-right', label: I18n.t('bottom_right') },
];

const VALUE_KEY_OPTIONS = [
  { value: 'identifier', label: I18n.t('project_identifier_text') },
  { value: 'id', label: I18n.t('project_id_number') },
];

const EMPTY_SETTINGS = {};

function DatasourceMappingRow({ mapping, index, setAttribute, allSettings, dashboardDatasources }) {
  const cols = useSelector(selectDatasourceCols(mapping.datasourceId));
  const dsOptions = useMemo(
    () => dashboardDatasources.map(ds => ({ value: ds.id, label: ds.name })),
    [dashboardDatasources]
  );
  const colOptions = useMemo(
    () => cols.map(c => ({ value: c.accessor, label: c.name || c.accessor })),
    [cols]
  );

  const updateMapping = (field, value) => {
    const updated = (allSettings.datasourceMappings || []).map((m, i) =>
      i === index ? { ...m, [field]: value } : m
    );
    setAttribute('settings.datasourceMappings', updated);
  };

  const removeMapping = () => {
    const updated = (allSettings.datasourceMappings || []).filter((_, i) => i !== index);
    setAttribute('settings.datasourceMappings', updated);
  };

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
      <div style={{ flex: 2, minWidth: 0 }}>
        <Select
          options={dsOptions}
          value={mapping.datasourceId}
          onChange={v => updateMapping('datasourceId', v)}
          placeholder={I18n.t('datasource')}
        />
      </div>
      <div style={{ flex: 2, minWidth: 0 }}>
        <Select
          options={colOptions}
          value={mapping.columnId}
          onChange={v => updateMapping('columnId', v)}
          placeholder={I18n.t('column')}
          isDisabled={!mapping.datasourceId}
        />
      </div>
      <div style={{ flex: 1.5, minWidth: 0 }}>
        <Select
          options={VALUE_KEY_OPTIONS}
          value={mapping.valueKey || 'identifier'}
          onChange={v => updateMapping('valueKey', v)}
          isClearable={false}
        />
      </div>
      <a onClick={removeMapping} style={{ cursor: 'pointer', flexShrink: 0 }}>
        <FaIcon type='trash' className='text-danger' />
      </a>
    </div>
  );
}

function PresetRow({ preset, index, setAttribute, allSettings }) {
  const { presets = [] } = useDashboardPresets();

  const updatePreset = (field, value) => {
    const updated = (allSettings.presets || []).map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    );
    setAttribute('settings.presets', updated);
  };

  const removePreset = () => {
    const updated = (allSettings.presets || []).filter((_, i) => i !== index);
    setAttribute('settings.presets', updated);
  };

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
      <input
        type='text'
        className='form-control form-control-sm'
        placeholder={I18n.t('label')}
        value={preset.label || ''}
        onChange={e => updatePreset('label', e.target.value)}
        style={{ flex: 1 }}
      />
      <div style={{ flex: 1 }}>
        <Select
          value={preset.id}
          onChange={(value) => updatePreset('id', value)}
          options={presets}
          placeholder={`${I18n.t('select')}...`}
          isClearable={false}
        />
      </div>
      <a onClick={removePreset} style={{ cursor: 'pointer', flexShrink: 0 }}>
        <FaIcon type='trash' className='text-danger' />
      </a>
    </div>
  );
}

function ProjectFilterWidgetSettings({ widgetId }) {
  const setAttribute = useSetWidgetAttribute(widgetId);
  const settings = useSelector(state => state.dashboard.widgets[widgetId]?.settings ?? EMPTY_SETTINGS);

  const dashboardDatasources = useSelector(
    state => Object.values(state.datasources.datasources || {}).map(ds => ({
      id: ds.id,
      name: ds.name,
    })),
    isEqual
  );

  const setAttr = (path, val) => setAttribute(`settings.${path}`, val);

  const addMapping = () => {
    const current = settings.datasourceMappings || [];
    setAttribute('settings.datasourceMappings', [...current, { datasourceId: null, columnId: null, valueKey: 'identifier' }]);
  };

  const addPreset = () => {
    const current = settings.presets || [];
    setAttribute('settings.presets', [...current, { id: null, label: '' }]);
  };

  const showAll = settings.showAll !== false;
  const showMyProjects = settings.showMyProjects !== false;


  return (
    <>
      <CollapsibleSection title={I18n.t('content_settings')}>
        <SettingsSection>
          <SelectSetting
            selectedObject={settings}
            setAttribute={() => setAttribute('settings.showAll', !showAll)}
            attr='showAll'
            label={I18n.t('show_all_button')}
            hideDescription
            isClearable={false}
            options={[
              { value: true, label: I18n.t('shown') },
              { value: false, label: I18n.t('hidden') },
            ]}
          />
          <SelectSetting
            selectedObject={settings}
            setAttribute={() => setAttribute('settings.showMyProjects', !showMyProjects)}
            attr='showMyProjects'
            label={I18n.t('show_my_projects_button')}
            isClearable={false}
            options={[
              { value: true, label: I18n.t('shown') },
              { value: false, label: I18n.t('hidden') },
            ]}
          />
          <SelectSetting
            selectedObject={settings}
            setAttribute={setAttr}
            attr='buttonSize'
            label={I18n.t('button_size')}
            hideDescription
            isClearable={false}
            options={BUTTON_SIZE_OPTIONS}
          />
          <SelectSetting
            selectedObject={settings}
            setAttribute={setAttr}
            attr='buttonAlignment'
            label={I18n.t('button_alignment')}
            hideDescription
            isClearable={false}
            options={ALIGNMENT_OPTIONS}
          />
          <ToggleSetting
            selectedObject={settings}
            setAttribute={setAttr}
            attr='singleProjectFilter'
            label={I18n.t('enable_single_project_filter')}
            hideDescription
          />
        </SettingsSection>
      </CollapsibleSection>

      <hr />

      <CollapsibleSection title={I18n.t('presets')} initialCollapsed={true}>
        <p className='text-muted' style={{ fontSize: 12 }}>
          {I18n.t('project_filter_preset_help')}
        </p>
        {(settings.presets || []).map((preset, i) => (
          <PresetRow
            key={i}
            preset={preset}
            index={i}
            setAttribute={setAttribute}
            allSettings={settings}
          />
        ))}
        <Button size='sm' onClick={addPreset}>
          <FaIcon type='plus' /> {I18n.t('add')}
        </Button>
      </CollapsibleSection>

      <hr />

      <CollapsibleSection title={I18n.t('datasource_mappings')} initialCollapsed={true}>
        <p className='text-muted' style={{ fontSize: 12 }}>
          {I18n.t('project_filter_mapping_help')}
        </p>
        <div style={{ display: 'flex', gap: 4, marginBottom: 4, fontSize: 11, fontWeight: 600 }}>
          <span style={{ flex: 2 }}>{I18n.t('datasource')}</span>
          <span style={{ flex: 2 }}>{I18n.t('column')}</span>
          <span style={{ flex: 1.5 }}>{I18n.t('match_value')}</span>
          <span style={{ width: 20 }} />
        </div>
        {(settings.datasourceMappings || []).map((mapping, i) => (
          <DatasourceMappingRow
            key={i}
            mapping={mapping}
            index={i}
            setAttribute={setAttribute}
            allSettings={settings}
            dashboardDatasources={dashboardDatasources}
          />
        ))}
        <Button size='sm' onClick={addMapping}>
          <FaIcon type='plus' /> {I18n.t('add')}
        </Button>
      </CollapsibleSection>
    </>
  );
}

export default ProjectFilterWidgetSettings;
